// useShopping — list switcher + items for the active list.
//
// First-mount strategy mirrors useTodos: fetch lists, pick the first
// non-archived (or create a default), load its items. Switching lists
// fetches fresh items for the new id. All mutations hit RPCs and
// update local state optimistically; on failure the next refresh
// reconciles.

import { useCallback, useEffect, useState } from "preact/hooks";
import { api, isOfflineError, peekCache, type ShoppingItem, type ShoppingList } from "./rpc";

// localStorage key for the last-active list id. Lets us reopen on the
// list the user was using instead of jumping back to whichever the
// server returns first.
const LAST_ACTIVE_KEY = "hub.shopping.activeListId";

function pickActiveList(lists: ShoppingList[]): ShoppingList | null {
  const active = lists.filter((x) => !x.archived);
  try {
    const saved = localStorage.getItem(LAST_ACTIVE_KEY);
    const id = saved ? Number(saved) : NaN;
    if (Number.isFinite(id)) {
      const found = active.find((x) => x.id === id);
      if (found) return found;
    }
  } catch { /* localStorage may be unavailable */ }
  return active[0] || null;
}

export interface UseShopping {
  lists: ShoppingList[];
  activeListId: number | null;
  setActiveListId: (id: number) => void;
  activeList: ShoppingList | null;
  items: ShoppingItem[];
  loading: boolean;
  error: string | null;
  add: (text: string, quantity?: string) => Promise<void>;
  toggle: (id: number) => Promise<void>;
  update: (id: number, patch: Partial<ShoppingItem>) => Promise<void>;
  remove: (id: number) => Promise<void>;
  clearPurchased: () => Promise<void>;
  createList: (name: string) => Promise<void>;
  deleteList: (listId: number) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useShopping(): UseShopping {
  const [lists, setLists] = useState<ShoppingList[]>([]);
  const [activeListId, _setActiveListId] = useState<number | null>(null);
  const [items, setItems] = useState<ShoppingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Bootstrap: fetch lists, pick which one to show. Pick order:
  //   1. last-active id from localStorage (if it still exists + isn't archived)
  //   2. first non-archived list
  // No auto-create: a household's Groceries list is seeded server-side on
  // household creation, and personal lists are user-created on demand.
  // Auto-creating a default here gave household-less users a stray
  // personal "Groceries" that then collided with the household's once
  // they joined/created one. When there are no lists, we land on the
  // empty state and the user makes one via "New list".
  const refresh = useCallback(async () => {
    setError(null);

    // Stale-while-revalidate: paint cached state immediately if we
    // have it, so the user sees their list at first paint instead of
    // a spinner. The network fetch below replaces it once fresh data
    // lands. Only show the spinner on a true cold start (no cache).
    let hadCache = false;
    try {
      const cachedLists = await peekCache<{ ok: true; lists: ShoppingList[] }>("shopping.listLists");
      if (cachedLists?.lists?.length) {
        hadCache = true;
        setLists(cachedLists.lists);
        const pick = pickActiveList(cachedLists.lists);
        if (pick) {
          _setActiveListId(pick.id);
          const cachedItems = await peekCache<{ ok: true; items: ShoppingItem[] }>("shopping.listItems", { listId: pick.id });
          if (cachedItems?.items) setItems(cachedItems.items);
        }
        setLoading(false);
      }
    } catch { /* swallow — fall through to network */ }
    if (!hadCache) setLoading(true);

    try {
      const l = await api.shoppingListLists();
      const pick = pickActiveList(l);
      setLists(l);
      if (!pick) {
        _setActiveListId(null);
        setItems([]);
        return;
      }
      _setActiveListId(pick.id);
      try { localStorage.setItem(LAST_ACTIVE_KEY, String(pick.id)); } catch { /* ignore */ }
      const fetched = await api.shoppingListItems(pick.id);
      setItems(fetched);
    } catch (e: any) {
      // If SWR painted from cache, suppress origin-unreachable noise.
      if (!hadCache || !isOfflineError(e)) {
        setError(e?.message || "failed to load shopping");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Switch list — load fresh items for the new id. Also persist the
  // pick so refresh() restores it next session.
  const setActiveListId = useCallback(async (id: number) => {
    _setActiveListId(id);
    try { localStorage.setItem(LAST_ACTIVE_KEY, String(id)); } catch { /* ignore */ }

    // SWR: show cached items for the target list immediately if we
    // have any, so list-switch feels instant. Spinner only on miss.
    let hadCache = false;
    try {
      const cached = await peekCache<{ ok: true; items: ShoppingItem[] }>("shopping.listItems", { listId: id });
      if (cached?.items) {
        hadCache = true;
        setItems(cached.items);
      }
    } catch { /* swallow */ }
    if (!hadCache) {
      setItems([]);
      setLoading(true);
    }

    try { setItems(await api.shoppingListItems(id)); }
    catch (e: any) {
      if (!hadCache || !isOfflineError(e)) {
        setError(e?.message || "failed to load items");
      }
    }
    finally { setLoading(false); }
  }, []);

  const activeList = lists.find((l) => l.id === activeListId) ?? null;

  const add = useCallback(async (text: string, quantity?: string) => {
    if (!activeListId) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    try {
      const item = await api.shoppingAddItem(activeListId, trimmed, quantity);
      setItems((prev) => [...prev, item]);
    } catch (e: any) { setError(e?.message || "add failed"); }
  }, [activeListId]);

  const toggle = useCallback(async (id: number) => {
    const cur = items.find((i) => i.id === id);
    if (!cur) return;
    // Optimistically flip purchased AND stamp purchasedAt so the
    // shopping UI's "Got it" sort surfaces this item at the top
    // immediately instead of waiting for the server response. The
    // server stamp will overwrite this with its own ISO timestamp.
    const optimisticPurchasedAt = !cur.purchased ? new Date().toISOString() : null;
    setItems((prev) => prev.map((i) => i.id === id
      ? { ...i, purchased: !i.purchased, purchasedAt: optimisticPurchasedAt }
      : i));
    try {
      const item = await api.shoppingUpdateItem(id, { purchased: !cur.purchased });
      setItems((prev) => prev.map((i) => i.id === id ? item : i));
    } catch (e: any) {
      setItems((prev) => prev.map((i) => i.id === id ? cur : i));
      setError(e?.message || "toggle failed");
    }
  }, [items]);

  const update = useCallback(async (id: number, patch: Partial<ShoppingItem>) => {
    setItems((prev) => prev.map((i) => i.id === id ? { ...i, ...patch } : i));
    try {
      const item = await api.shoppingUpdateItem(id, patch);
      setItems((prev) => prev.map((i) => i.id === id ? item : i));
    } catch (e: any) { setError(e?.message || "update failed"); }
  }, []);

  const remove = useCallback(async (id: number) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    try { await api.shoppingDeleteItem(id); }
    catch (e: any) { setError(e?.message || "delete failed"); }
  }, []);

  const clearPurchased = useCallback(async () => {
    if (!activeListId) return;
    try {
      await api.shoppingClearPurchased(activeListId);
      setItems((prev) => prev.filter((i) => !i.purchased));
    } catch (e: any) { setError(e?.message || "clear failed"); }
  }, [activeListId]);

  const createList = useCallback(async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const list = await api.shoppingCreateList(trimmed);
      setLists((prev) => [...prev, list]);
      await setActiveListId(list.id);
    } catch (e: any) { setError(e?.message || "create list failed"); }
  }, [setActiveListId]);

  const deleteList = useCallback(async (listId: number) => {
    try {
      await api.shoppingDeleteList(listId);
      const remaining = lists.filter((l) => l.id !== listId);
      setLists(remaining);
      // If we just nuked the active list, switch to another or
      // refresh-with-bootstrap to recreate the default.
      if (activeListId === listId) {
        const next = remaining.find((l) => !l.archived);
        if (next) {
          await setActiveListId(next.id);
        } else {
          await refresh();
        }
      }
    } catch (e: any) { setError(e?.message || "delete list failed"); }
  }, [lists, activeListId, setActiveListId, refresh]);

  return {
    lists, activeListId, setActiveListId, activeList,
    items, loading, error,
    add, toggle, update, remove, clearPurchased, createList, deleteList, refresh,
  };
}
